import { useContext, useEffect, useCallback } from 'react';
import { StoreContext } from '../../context/StoreContext';
import storeService from '../services/store.service';

const ALL_STORES_LIMIT = 100000;

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const extractStoreItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.items)) return payload.items;

  const node = payload?.data ?? payload;
  if (Array.isArray(node)) return node;
  if (Array.isArray(node?.docs)) return node.docs;

  return [];
};

const normalizeStore = (store) => {
  if (!store) return null;

  const lat = toNumberOrNull(store.lat ?? store.latitude);
  const lng = toNumberOrNull(store.lng ?? store.longitude ?? store.lon);
  const isActive =
    store.is_active === true ||
    store.is_active === 1 ||
    String(store.status || '').toLowerCase() === 'active' ||
    store.is_active === undefined;

  return {
    id: store.uuid || store.id || store._id,
    uuid: store.uuid || store.id || store._id,
    name: store.name || '',
    address: store.address || '',
    city: store.city || store.location || '',
    state: store.state || '',
    pincode: store.pincode ? String(store.pincode) : '',
    country: store.country || '',
    mobile: store.mobile || store.phone || '',
    phone: store.phone || store.mobile || '',
    lat,
    lng,
    hasCoordinates: lat !== null && lng !== null,
    is_active: isActive,
  };
};

export const useStores = () => {
  const { stores, loading, error, setStores, setLoading, setError } = useContext(StoreContext);

  const fetchStores = useCallback(async (params = { page: 0, limit: ALL_STORES_LIMIT, search: '' }) => {
    try {
      setLoading(true);
      setError(null);
      const data = await storeService.userList(params);
      const normalizedStores = extractStoreItems(data)
        .map(normalizeStore)
        .filter((store) => store && store.is_active);

      setStores(normalizedStores);
      return normalizedStores;
    } catch (err) {
      setError(err.message || 'Failed to load stores');
      setStores([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [setStores, setLoading, setError]);

  // Fetch the full public store list on mount. The store locator needs all
  // locations, not the API's smaller default page.
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const searchStores = useCallback((query) => {
    if (!query) return stores;
    
    const lowerQuery = query.toLowerCase();
    return stores.filter(store =>
      store.name?.toLowerCase().includes(lowerQuery) ||
      store.address?.toLowerCase().includes(lowerQuery) ||
      store.city?.toLowerCase().includes(lowerQuery)
    );
  }, [stores]);

  const getStoreById = useCallback((id) => {
    return stores.find(s => s.id === id || s.uuid === id);
  }, [stores]);

  return {
    stores,
    loading,
    error,
    fetchStores,
    searchStores,
    getStoreById,
  };
};
